import { themeGet } from '@styled-system/theme-get';
import { array, arrayOf, bool, func, oneOfType, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import MaskedInput from 'react-text-mask';
import { useTheme } from 'styled-components/macro';

import { Box, FieldError, Input, Label, OptionalField } from '@olist/united';

export const getErrors = (meta, initialErrors) => {
  const getClientErrors = meta.touched && meta.error;
  const getServerErrors = !meta.dirtySinceLastSubmit && (meta.submitError || initialErrors);
  return getClientErrors || getServerErrors;
};

const InputLabelError = ({
  initialErrors,
  label,
  mask,
  name,
  onBlur,
  optionalLabel,
  showMessages,
  ...rest
}) => {
  const theme = useTheme();
  const handleBlur = (input, meta) => (event) => {
    input.onBlur(event);
    onBlur(event, input, meta);
  };

  return (
    <Field {...rest} name={name}>
      {({ input, meta }) => {
        const defaultColor = themeGet('olist.color.neutral.stronger')({ theme });
        const errorColor = themeGet('olist.color.alert.base')({ theme });
        const errors = getErrors(meta, initialErrors[input.name]);
        const hasErrors = !!errors;

        return (
          <>
            <Label color={errors?.length ? errorColor : defaultColor} htmlFor={input.name}>
              {label}
              {!!optionalLabel && <OptionalField ml={1}>{optionalLabel}</OptionalField>}
            </Label>
            {mask ? (
              <MaskedInput
                {...rest}
                {...input}
                error={hasErrors}
                id={input.name}
                mask={mask}
                onBlur={handleBlur(input, meta)}
                render={(ref, props) => <Input ref={ref} {...props} />}
              />
            ) : (
              <Input
                {...rest}
                {...input}
                error={hasErrors}
                id={input.name}
                onBlur={handleBlur(input, meta)}
              />
            )}

            {showMessages && hasErrors && (
              <Box mt={2}>
                <FieldError messages={errors} />
              </Box>
            )}
          </>
        );
      }}
    </Field>
  );
};

InputLabelError.defaultProps = {
  initialErrors: {},
  mask: false,
  onBlur: () => {},
  showMessages: true,
  optionalLabel: '',
};

InputLabelError.propTypes = {
  initialErrors: arrayOf(string),
  label: string.isRequired,
  mask: oneOfType([
    array,
    func,
    bool,
    shape({
      mask: oneOfType([array, func]),
      pipe: func,
    }),
  ]),
  name: string.isRequired,
  onBlur: func,
  optionalLabel: string,
  showMessages: bool,
};

export default InputLabelError;
